import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { apiClient } from "../../services/api";

/**
 * Morning check-in card. Visible 05:00–11:00 IST only, hidden after today's
 * check-in is completed. Sequential Q1 → Q2 → Q3 with no Next button.
 * On completion, POSTs to /api/checkin/morning and collapses to a summary.
 */
function nowIstHour() {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const ist = new Date(utc + 5.5 * 3600000);
  return ist.getHours();
}

const QUESTIONS = [
  { key: "sleep", prompt: "How did you sleep?", low: "Poor", high: "Great" },
  { key: "soreness", prompt: "How sore are you?", low: "Not at all", high: "Very" },
  { key: "energy", prompt: "How is your energy?", low: "Low", high: "High" },
];

export default function MorningCheckinCard() {
  const [completedToday, setCompletedToday] = useState(null); // null=loading, true, false
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [awarded, setAwarded] = useState(0);
  const [justCompleted, setJustCompleted] = useState(false);

  useEffect(() => {
    apiClient
      .get("/checkin/morning/today")
      .then((r) => setCompletedToday(!!r.data))
      .catch(() => setCompletedToday(false));
  }, []);

  const hour = nowIstHour();
  const inWindow = hour >= 5 && hour < 11;
  if (completedToday === null) return null;
  if (completedToday || justCompleted) {
    // Collapsed one-line summary for the rest of the day.
    return (
      <div
        data-testid="checkin-summary"
        style={{
          background: "rgba(34,197,94,0.08)",
          border: "1px solid rgba(34,197,94,0.30)",
          borderRadius: 12,
          padding: "10px 14px",
          fontFamily: "Barlow, sans-serif",
          fontSize: 13,
          color: "var(--text-primary,#0C0C0B)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>Morning check-in complete{awarded ? ` · +${awarded} FP` : ""}</span>
        <span style={{ color: "#15803D", fontWeight: 700, fontSize: 11 }}>DONE</span>
      </div>
    );
  }
  if (!inWindow) return null;

  const onAnswer = async (value) => {
    const q = QUESTIONS[step];
    const next = { ...answers, [q.key]: value };
    setAnswers(next);
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
      return;
    }
    // Final answer — submit.
    setBusy(true);
    try {
      const { data } = await apiClient.post("/checkin/morning", next);
      setAwarded(data?.fitpoints_awarded || 0);
      setJustCompleted(true);
      if (data?.fitpoints_awarded) {
        toast.success(`+${data.fitpoints_awarded} FitPoints`);
      }
    } catch {
      toast.error("Could not save check-in");
    } finally {
      setBusy(false);
    }
  };

  const q = QUESTIONS[step];
  return (
    <motion.section
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="checkin-card"
      style={{
        background: "#FFFFFF",
        border: "1px solid var(--border, rgba(12,12,11,0.08))",
        borderRadius: 16,
        padding: 16,
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "#A37B00" }}>
        Morning Check-in · Step {step + 1} of 3
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={q.key}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.25 }}
        >
          <h3
            style={{
              fontFamily: "Barlow Condensed, sans-serif",
              fontWeight: 700,
              fontSize: 22,
              marginTop: 6,
              color: "var(--text-primary, #0C0C0B)",
            }}
          >
            {q.prompt}
          </h3>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            {[1, 2, 3, 4, 5].map((v) => (
              <button
                key={v}
                onClick={() => !busy && onAnswer(v)}
                disabled={busy}
                data-testid={`checkin-${q.key}-${v}`}
                style={{
                  flex: 1,
                  height: 44,
                  borderRadius: 100,
                  background: "var(--canvas2, #EAE8E5)",
                  border: "1px solid var(--border, rgba(12,12,11,0.10))",
                  fontFamily: "Barlow Condensed, sans-serif",
                  fontWeight: 700,
                  fontSize: 18,
                  color: "var(--text-primary,#0C0C0B)",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
              >
                {v}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontFamily: "Barlow, sans-serif", fontSize: 10, color: "var(--text-tertiary,#8A8884)" }}>
            <span>{q.low}</span>
            <span>{q.high}</span>
          </div>
        </motion.div>
      </AnimatePresence>
    </motion.section>
  );
}
